<template>
    <div id="data-set-page">
        <div class="container-fluid">
            <div class="data-set-form-container">
                <loader v-if="loading" />
                <form v-else @submit.prevent="handleSubmit">
                    <div class="d-flex justify-content-between align-items-center mb-4">
                        <h1 class="page-title">{{ type === "edit" ? t('dataSets-form-edit_title') : t('dataSets-form-new_title') }}</h1>
                        <router-link to="/data-sets" class="btn btn-outline-secondary btn-sm">
                            {{ t('dataSets-form-back') }}
                        </router-link>
                    </div>

                    <!-- Basic Information Card -->
                    <div class="data-set-card">
                        <h2>{{ t('dataSets-form-basic_info') }}</h2>
                        <div class="field">
                            <label for="name">{{ t('dataSets-form-name_label') }}</label>
                            <div class="input-group">
                                <div class="input-group-prepend">
                                    <span class="input-group-text">
                                        <i class="fas fa-database"></i>
                                    </span>
                                </div>
                                <input
                                    id="name"
                                    name="name"
                                    v-model="formData.name"
                                    type="text"
                                    class="form-control"
                                    :placeholder="t('dataSets-form-name_placeholder')"
                                    autocomplete="off"
                                    :class="{ 'is-invalid': errors.name }"
                                />
                            </div>
                            <div class="invalid-feedback" v-if="errors.name">{{ errors.name }}</div>
                        </div>

                        <div class="field mt-4">
                            <label for="description">{{ t('dataSets-form-description_label') }}</label>
                            <textarea
                                id="description"
                                name="description"
                                v-model="formData.description"
                                class="form-control"
                                :placeholder="t('dataSets-form-description_placeholder')"
                                maxlength="250"
                                rows="3"
                                autocomplete="off"
                                :class="{ 'is-invalid': errors.description }"
                            />
                            <div class="invalid-feedback" v-if="errors.description">{{ errors.description }}</div>
                            <small class="form-text text-muted">
                                {{ formData.description.length }}/250 {{ t('dataSets-form-characters') }}
                            </small>
                        </div>
                    </div>

                    <!-- Data Set Structure Card -->
                    <div class="data-set-card">
                        <div class="data-set-card-header">
                            <div class="header-title">
                                <h2>{{ t('dataSets-form-data_structure') }}</h2>
                                <small>{{ t('dataSets-form-data_structure_help') }}</small>
                            </div>
                            <div class="header-actions">
                                <button
                                    type="button"
                                    class="btn btn-ai"
                                    @click="openAIModal"
                                    :disabled="loadingAI"
                                >
                                    <span>
                                    {{ loadingAI ? t('dataSets-form-processing') : t('dataSets-form-edit_with_ai') }}
                                    </span>
                                </button>
                            </div>
                        </div>

                        <!-- Data Table -->
                        <div class="data-table-container">
                            <div class="table-responsive">
                                <table class="table table-bordered data-table">
                                    <thead>
                                        <tr>
                                            <th class="key-column">{{ t('dataSets-form-key_column') }}</th>
                                            <th v-for="(header, index) in headers" :key="index" class="data-column">
                                                <div class="column-header">
                                                    <input
                                                        :id="`header-${index}`"
                                                        :name="`header-${index}`"
                                                        v-model="headers[index]"
                                                        class="form-control form-control-sm"
                                                        :placeholder="t('dataSets-form-column_name_placeholder')"
                                                        autocomplete="off"
                                                        :class="{ 'is-invalid': !header.trim() }"
                                                    />
                                                    <button
                                                        type="button"
                                                        @click="removeHeader(index)"
                                                        class="btn btn-outline-danger btn-sm ml-2"
                                                        :disabled="headers.length <= 1"
                                                    >
                                                        <i class="fa fa-times"></i>
                                                    </button>
                                                </div>
                                            </th>
                                            <th class="actions-column">
                                                <button
                                                    type="button"
                                                    @click="addHeader"
                                                    class="btn btn-primary btn-sm"
                                                    :disabled="!canAddHeader"
                                                >
                                                    <i class="fa fa-plus"></i>
                                                </button>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="key in orderedDataSetKeys" :key="key" class="data-row">
                                            <td class="key-cell">
                                                <input
                                                    :id="`key-${key}`"
                                                    :name="`key-${key}`"
                                                    :value="key"
                                                    @blur="renameKey(key, $event.target.value)"
                                                    @keydown.enter="$event.target.blur()"
                                                    class="form-control form-control-sm"
                                                    :readonly="key === 'default'"
                                                    :placeholder="key === 'default' ? t('dataSets-form-default_value') : t('dataSets-form-key_column')"
                                                    autocomplete="off"
                                                />
                                            </td>
                                            <td v-for="(header, index) in headers" :key="index" class="data-cell">
                                                <textarea
                                                    :id="`data-${key}-${index}`"
                                                    :name="`data-${key}-${index}`"
                                                    v-model="formData.data_set[key][index]"
                                                    class="form-control form-control-sm"
                                                    :placeholder="t('dataSets-form-value_placeholder')"
                                                    rows="2"
                                                    autocomplete="off"
                                                />
                                            </td>
                                            <td class="actions-cell">
                                                <button
                                                    type="button"
                                                    @click="removeKey(key)"
                                                    class="btn btn-outline-danger btn-sm"
                                                    v-if="key !== 'default'"
                                                >
                                                    <i class="fa fa-trash"></i>
                                                </button>
                                            </td>
                                        </tr>
                                        <tr class="add-row">
                                            <td class="key-cell">
                                                <input
                                                    id="new-key"
                                                    name="new-key"
                                                    v-model="newKey"
                                                    class="form-control form-control-sm"
                                                    :placeholder="t('dataSets-form-new_key_placeholder')"
                                                    autocomplete="off"
                                                />
                                            </td>
                                            <td v-for="(header, index) in headers" :key="index" class="data-cell">
                                                <textarea
                                                    :id="`new-value-${index}`"
                                                    :name="`new-value-${index}`"
                                                    v-model="newValues[index]"
                                                    class="form-control form-control-sm"
                                                    :placeholder="t('dataSets-form-value_placeholder')"
                                                    rows="2"
                                                    autocomplete="off"
                                                />
                                            </td>
                                            <td class="actions-cell">
                                                <button
                                                    type="button"
                                                    @click="addKey"
                                                    class="btn btn-success btn-sm"
                                                    :disabled="!canAddNewKey"
                                                >
                                                    <i class="fa fa-plus"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div class="validation-errors" v-if="errors.data_set || errors.headers || errors.duplicateKey">
                            <div class="alert alert-danger">
                                <ul class="mb-0">
                                    <li v-if="errors.data_set">{{ errors.data_set }}</li>
                                    <li v-if="errors.headers">{{ errors.headers }}</li>
                                    <li v-if="errors.duplicateKey">{{ errors.duplicateKey }}</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <!-- Actions -->
                    <div class="actions-container">
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            @click="$router.push('/data-sets')"
                        >
                            {{ t('dataSets-form-cancel') }}
                        </button>
                        <button
                            type="submit"
                            class="btn btn-primary btn-save"
                            :disabled="!isValid || saveLoading"
                        >
                            {{ saveLoading ? t('dataSets-form-saving') : t('dataSets-form-save_button') }}
                        </button>
                    </div>
                </form>
            </div>
        </div>

        <!-- AI Edit Modal -->
        <modal id="ai-edit-modal" ref="aiModal" size="lg">
            <template slot="title">
                <div class="d-flex align-items-center">
                    {{ t('dataSets-form-ai_modal_title') }}
                </div>
            </template>
            <template slot="body">
                <div class="ai-modal-content">
                    <div class="current-schema mb-4">
                        <h5>{{ t('dataSets-form-ai_modal_current') }}</h5>
                        <div class="schema-preview">
                            <div class="schema-info">
                                <strong>{{ t('dataSets-form-ai_modal_columns') }}</strong> {{ headers.join(', ') }}
                            </div>
                            <div class="schema-info">
                                <strong>{{ t('dataSets-form-ai_modal_rows') }}</strong> {{ Object.keys(formData.data_set).filter(k => k !== 'default').length }}
                            </div>
                        </div>
                    </div>

                    <div class="ai-prompt-section" v-if="!processingAI">
                        <label for="ai-prompt">{{ t('dataSets-form-ai_modal_instructions') }}</label>
                        <textarea
                            id="ai-prompt"
                            v-model="aiPrompt"
                            class="form-control"
                            :placeholder="t('dataSets-form-ai_modal_placeholder')"
                            rows="12"
                            maxlength="100000"
                            autocomplete="off"
                        />
                        <small class="form-text text-muted">
                            {{ aiPrompt.length }}/100.000 {{ t('dataSets-form-characters') }}
                        </small>
                    </div>

                    <div v-if="processingAI" class="ai-loading">
                        <div class="text-center w-100 d-flex flex-column align-items-center">
                            <sentence-swapper :sentences="t('dataSets-form-ai_loading_messages')" />
                            <div class="mt-4" style="width:80%; max-width: 400px;">
                                <ai-loader :height="12" :duration="30000" />
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button type="button" class="btn btn-outline-secondary" @click="closeAIModal">
                    {{ t('dataSets-form-cancel') }}
                </button>
                <button
                    type="button"
                    class="btn btn-primary"
                    @click="processAIEdit"
                    :disabled="!aiPrompt.trim() || processingAI"
                >
                    {{ processingAI ? t('dataSets-form-processing') : t('dataSets-form-ai_modal_generate') }}
                </button>
            </template>
        </modal>
    </div>
</template>

<script>
import keviApi from "../keviApi";
import Modal from "../components/modal/modal.vue";
import SentenceSwapper from "../components/ui/sentence-swapper.vue";
import AiLoader from "../components/ui/ai-loader.vue";

export default {
    name: "data-set",
    components: {
        Modal,
        SentenceSwapper,
        AiLoader
    },
    props: {
        type: {
            type: String,
            default: "new",
        },
    },
    data() {
        return {
            loading: true,
            saveLoading: false,
            newKey: "",
            newValues: [],
            headers: [""],
            formData: {
                name: "",
                description: "",
                data_set: {
                    "default": [""],
                },
            },
            errors: {
                name: "",
                description: "",
                data_set: "",
                headers: "",
                duplicateKey: "",
            },
            // AI related
            aiPrompt: "",
            loadingAI: false,
            processingAI: false,
        };
    },
    created() {
        document.getElementsByTagName("title")[0].innerText = "Kevi | " + this.t('dataSets-title');
    },
    mounted() {
        if (this.type === "edit") {
            this.loadDataSet();
        } else {
            this.loading = false;
        }
    },
    watch: {
        "formData.name"(val) {
            this.errors.name = val.trim() ? "" : this.t('dataSets-form-validation_name_required');
        },
        "formData.description"(val) {
            if (val.length > 250) {
                this.errors.description = this.t('dataSets-form-validation_description_max');
            } else {
                this.errors.description = "";
            }
        },
        "formData.data_set": {
            deep: true,
            handler() {
                this.validateDataSet();
            },
        },
        headers: {
            handler() {
                this.validateHeaders();
            },
            deep: true
        },
        newKey() {
            // Clear duplicate key error when user types in new key field
            if (this.errors.duplicateKey) {
                this.errors.duplicateKey = "";
            }
        }
    },
    computed: {
        isValid() {
            return this.formData.name.trim() &&
                this.formData.description.trim() &&
                this.formData.description.length <= 250 &&
                !this.errors.data_set &&
                !this.errors.headers &&
                !this.errors.duplicateKey;
        },
        canAddNewKey() {
            return this.newKey.trim() &&
                   !Object.prototype.hasOwnProperty.call(this.formData.data_set, this.newKey.trim());
        },
        canAddHeader() {
            return true;
        },
        orderedDataSetKeys() {
            const keys = Object.keys(this.formData.data_set);
            const filteredKeys = keys.filter(key => key !== "default");
            return ["default", ...filteredKeys];
        },
    },
    methods: {
        async loadDataSet() {
            try {
                const options = keviApi.getOptions(`data-sets/${this.$route.params.id}`);
                const response = await this.$http(options);

                this.formData.name = response.data.dataSet.name;
                this.formData.description = response.data.dataSet.description;
                const dataSet = response.data.dataSet.data_set;
                this.headers = dataSet.headers || [""];
                delete dataSet.headers;

                for (const key in dataSet) {
                    this.$set(this.formData.data_set, key, dataSet[key]);
                }
            } catch {
                this.$store.dispatch("notification/warning", this.t('dataSets-form-error_loading'));
                this.$router.push("/data-sets");
            } finally {
                this.loading = false;
            }
        },

        validateDataSet() {
            const dataSetKeys = Object.keys(this.formData.data_set).filter(key => key !== "default");
            if (!dataSetKeys.length) {
                this.errors.data_set = this.t('dataSets-form-validation_empty_dataset');
            } else {
                this.errors.data_set = "";
            }
        },

        validateHeaders() {
            if (!this.headers.every(header => header.trim() !== "")) {
                this.errors.headers = this.t('dataSets-form-validation_empty_headers');
            } else {
                this.errors.headers = "";
            }
        },

        async handleSubmit() {
            if (!this.isValid) return;

            this.saveLoading = true;
            try {
                const endpoint = this.type === "edit" ? `data-sets/${this.$route.params.id}` : "data-sets";
                const method = this.type === "edit" ? "PUT" : "POST";

                const dataSet = {
                    headers: this.headers,
                    ...this.formData.data_set,
                };

                // Clean up empty values
                for (const key in dataSet) {
                    if (Array.isArray(dataSet[key])) {
                        dataSet[key] = dataSet[key].map(value => value.trim() === "" ? "" : value);
                        while (dataSet[key].length < this.headers.length) {
                            dataSet[key].push("");
                        }
                    }
                }

                const options = keviApi.getOptions(endpoint, method, {
                    ...this.formData,
                    data_set: dataSet,
                });

                await this.$http(options);

                const textCondition = this.type === "edit" ? this.t('dataSets-form-edit_success') : this.t('dataSets-form-create_success');
                this.$store.dispatch("notification/success", textCondition);
                this.$router.push("/data-sets");
            } catch (error) {
                console.error(error);
                this.$store.dispatch("notification/error", this.t('dataSets-form-error_saving'));
            } finally {
                this.saveLoading = false;
            }
        },

        addKey() {
            const trimmedKey = this.newKey.trim();
            if (!trimmedKey) {
                return;
            }

            if (Object.prototype.hasOwnProperty.call(this.formData.data_set, trimmedKey)) {
                this.errors.duplicateKey = this.t('dataSets-form-validation_duplicate_key', [trimmedKey]);
                return;
            }

            this.errors.duplicateKey = "";

            while (this.newValues.length < this.headers.length) {
                this.newValues.push("");
            }

            this.$set(this.formData.data_set, trimmedKey, [...this.newValues]);
            this.newKey = "";
            this.newValues = new Array(this.headers.length).fill("");
        },

        renameKey(oldKey, newKey) {
            if (oldKey === "default") return;
            if (typeof newKey !== "string") return;

            const trimmed = newKey.trim();

            if (trimmed === oldKey) return;

            if (!trimmed) return;

            if (Object.prototype.hasOwnProperty.call(this.formData.data_set, trimmed)) {
                this.errors.duplicateKey = this.t('dataSets-form-validation_duplicate_key', [trimmed]);
                this.$nextTick(() => {
                    const input = document.getElementById(`key-${oldKey}`);
                    if (input) {
                        input.value = oldKey;
                    }
                });
                return;
            }

            this.errors.duplicateKey = "";

            const newDataSet = {};

            if (this.formData.data_set.default) {
                newDataSet.default = this.formData.data_set.default;
            }

            const keys = Object.keys(this.formData.data_set);
            keys.forEach(key => {
                if (key === "default") {
                    return;
                } else if (key === oldKey) {
                    newDataSet[trimmed] = this.formData.data_set[oldKey];
                } else {
                    newDataSet[key] = this.formData.data_set[key];
                }
            });

            this.formData.data_set = newDataSet;
        },

        removeKey(key) {
            if (key !== "default") {
                this.$delete(this.formData.data_set, key);
            }
        },

        addHeader() {
            this.headers.push("");
            for (const key in this.formData.data_set) {
                this.formData.data_set[key].push("");
            }
        },

        removeHeader(index) {
            if (this.headers.length > 1) {
                this.headers.splice(index, 1);
                for (const key in this.formData.data_set) {
                    this.formData.data_set[key].splice(index, 1);
                }
            }
        },

        // AI Methods
        openAIModal() {
            this.aiPrompt = "";
            this.$refs.aiModal.open();
        },

        closeAIModal() {
            this.$refs.aiModal.close();
            this.aiPrompt = "";
        },

        async processAIEdit() {
            if (!this.aiPrompt.trim()) return;

            this.processingAI = true;
            try {
                const currentSchema = {
                    headers: this.headers,
                    data_set: this.formData.data_set
                };

                const options = keviApi.getOptions("data-sets/ai-edit", "POST", {
                    currentSchema,
                    prompt: this.aiPrompt
                });

                const response = await this.$http(options);

                if (response.status === 200) {
                    if (response.body.status === "Success" && response.body.schema) {
                        const updatedSchema = response.body.schema;

                        // Apply the updated schema
                        this.headers = updatedSchema.headers;
                        this.formData.data_set = updatedSchema.data_set;

                        this.$store.dispatch("notification/success", this.t('dataSets-form-ai_success'));
                        this.closeAIModal();
                    } else {
                        console.error("Response body:", response.body);
                        throw new Error(response.body.message || this.t('dataSets-form-ai_error_process'));
                    }
                } else {
                    throw new Error(this.t('dataSets-form-ai_error_connection'));
                }
            } catch (error) {
                console.error("AI Edit Error:", error);
                this.$store.dispatch("notification/error",
                    error.message || this.t('dataSets-form-ai_error_generic'));
            } finally {
                this.processingAI = false;
            }
        },
        t: function (term, replaces) {
            return this.$root.t(term, replaces);
        },
    }
};
</script>

<style lang="scss" scoped>
:root {
    --kevi-green: #11875a;
    --kevi-green-light: #e8f4ef;
    --kevi-gray-light: #f7f9fa;
    --kevi-red: #f44336;
    --kevi-green-dark: #0d6a44;
}

.data-set-form-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 24px 0;
}

.page-title {
    font-size: 24px;
    font-weight: 600;
    margin-bottom: 0;
    color: #333;
}

.data-set-card {
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    padding: 24px;
    margin-bottom: 24px;

    h2 {
        font-size: 18px;
        font-weight: 600;
        margin-bottom: 8px;
        color: #333;
    }

    small {
        display: block;
        color: #6c757d;
        margin-bottom: 16px;
        font-size: 14px;
    }
}

.data-set-card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 24px;

    .header-title {
        h2 {
            margin-bottom: 4px;
        }
    }

    .header-actions {
        flex-shrink: 0;
    }

    @media (max-width: 767px) {
        flex-direction: column;
        align-items: flex-start;

        .header-actions {
            margin-top: 16px;
        }
    }
}

.field {
    label {
        font-weight: 500;
        color: #495057;
        margin-bottom: 8px;
        display: block;
    }

    .input-group-text {
        background-color: var(--kevi-green-light);
        border-color: #ced4da;
        color: var(--kevi-green);
    }

    .form-control {
        border-color: #ced4da;

        &:focus {
            border-color: var(--kevi-green);
            box-shadow: 0 0 0 0.2rem rgba(17, 135, 90, 0.25);
        }

        &.is-invalid {
            border-color: var(--kevi-red);
        }
    }
}

.btn-ai {
    background-color: var(--kevi-green);
    border-color: var(--kevi-green);
    color: white;
    padding: 8px 16px;
    font-weight: 500;

    &:hover,
    &:focus {
        background-color: var(--kevi-green-dark);
        border-color: var(--kevi-green-dark);
        color: white;
    }

    &:disabled {
        opacity: 0.6;
    }
}

.data-table-container {
    margin-top: 16px;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    overflow: hidden;

    .table-responsive {
        overflow-x: auto;
        scrollbar-width: thin;
        scrollbar-color: #ced4da #f8f9fa;

        &::-webkit-scrollbar {
            height: 8px;
        }

        &::-webkit-scrollbar-track {
            background: #f8f9fa;
        }

        &::-webkit-scrollbar-thumb {
            background: #ced4da;
            border-radius: 4px;
        }

        &::-webkit-scrollbar-thumb:hover {
            background: #adb5bd;
        }
    }
}

.data-table {
    margin-bottom: 0;
    min-width: 100%;

    th, td {
        vertical-align: middle;
        padding: 12px;
        border-color: #e9ecef;
    }

    .key-column {
        width: 250px;
        min-width: 250px;
        background-color: #f8f9fa;
        font-weight: 600;
    }

    .data-column {
        min-width: 300px;
        width: 300px;
    }

    .actions-column {
        width: 40px;
        min-width: 40px;
        text-align: center;
    }

    .column-header {
        display: flex;
        align-items: center;
        gap: 8px;

        .form-control {
            flex: 1;
        }

        .btn {
            padding: 0.25rem 0.5rem;
            font-size: 0.75rem;
        }
    }

    .key-cell {
        background-color: #f8f9fa;
        font-weight: 500;
    }

    .data-cell {
        .form-control {
            font-size: 0.875rem;
            resize: vertical;
        }
    }

    .actions-cell {
        text-align: center;

        .btn {
            padding: 0.25rem 0.5rem;
            font-size: 0.75rem;
        }
    }

    .add-row {
        background-color: #f8f9fa;

        .key-cell {
            background-color: #e9ecef;
        }
    }
}

.validation-errors {
    margin-top: 16px;
}

.actions-container {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 32px;

    .btn-save {
        background-color: var(--kevi-green);
        border-color: var(--kevi-green);
        padding: 10px 24px;
        font-weight: 500;
        min-width: 200px;

        &:hover,
        &:focus {
            background-color: var(--kevi-green-dark);
            border-color: var(--kevi-green-dark);
        }

        &:disabled {
            opacity: 0.5;
        }
    }

    @media (max-width: 767px) {
        flex-direction: column;

        .btn {
            width: 100%;
        }
    }
}

// AI Modal Styles
.ai-modal-content {
    .current-schema {
        .schema-preview {
            background-color: var(--kevi-gray-light);
            border-radius: 6px;
            padding: 16px;
            margin-top: 8px;

            .schema-info {
                margin-bottom: 8px;
                font-size: 14px;

                &:last-child {
                    margin-bottom: 0;
                }
            }
        }
    }

    .ai-prompt-section {
        label {
            font-weight: 500;
            color: #495057;
            margin-bottom: 8px;
            display: block;
        }

        textarea {
            font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
            font-size: 14px;
            line-height: 1.5;
        }
    }

    .ai-instructions {
        .alert {
            border-left: 4px solid var(--kevi-green);
        }
    }
}

.ai-loading {
    padding: 20px;
    background-color: var(--kevi-gray-light);
    border-radius: 8px;
    border: 1px solid #e9ecef;
}

// Responsive adjustments
@media (max-width: 1200px) {
    .data-table {
        .data-column {
            min-width: 150px;
        }
    }
}

@media (max-width: 768px) {
    .data-set-form-container {
        padding: 16px;
    }

    .data-set-card {
        padding: 16px;
    }

    .data-table {
        font-size: 14px;

        th, td {
            padding: 8px;
        }

        .key-column {
            width: 120px;
        }

        .data-column {
            min-width: 120px;
        }

        .actions-column {
            width: 100px;
        }
    }
}
</style>
